import {
  StackFilterDetails,
  StackFilterDocument,
} from 'src/services/documents/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Add type for the mutation function
type StackFilterMutationFn = (params: {
  stackFilterId: string;
  name: string;
  documentTypes: StackFilterDocument[];
  isOrganization: boolean;
  visibility: 'public' | 'private';
}) => void;

interface DocumentViewState {
  unsavedStackFilters: Record<string, StackFilterDetails>;
  pendingStackFilters: Record<string, StackFilterDetails>;
  currentStackFilterId: string;
  actions: {
    setStackFilter: (stackFilter: StackFilterDetails) => void;
    updateStackFilter: (
      stackFilter: StackFilterDetails,
      mutateFn: StackFilterMutationFn,
    ) => void;
    setCurrentStackFilterId: (stackFilterId: string) => void;
    resetStackFilter: (stackFilterId: string) => void;
    resetPendingStackFilter: (stackFilterId: string) => void;
    setPendingState: (stackFilterId: string, pending?: boolean) => void;
  };
}

export const useDocumentViewStore = create<DocumentViewState>()(
  persist(
    (set) => ({
      currentStackFilterId: '',
      unsavedStackFilters: {},
      pendingStackFilters: {},
      actions: {
        setStackFilter: (stackFilter: StackFilterDetails) =>
          set((state) => ({
            unsavedStackFilters: {
              ...state.unsavedStackFilters,
              [stackFilter.id]: stackFilter,
            },
          })),
        updateStackFilter: (
          stackFilter: StackFilterDetails,
          mutateFn: StackFilterMutationFn,
        ) => {
          // Update local state
          set((state) => ({
            unsavedStackFilters: {
              ...state.unsavedStackFilters,
              [stackFilter.id]: stackFilter,
            },
          }));

          // Persist to backend
          mutateFn({
            stackFilterId: stackFilter.id,
            name: stackFilter.name,
            documentTypes: stackFilter.documentTypes,
            isOrganization: stackFilter.isOrganization,
            visibility: stackFilter.isOrganization ? 'public' : 'private',
          });
        },
        setCurrentStackFilterId: (stackFilterId: string) =>
          set(() => ({
            currentStackFilterId: stackFilterId,
          })),
        resetStackFilter: (stackFilterId: string) => {
          set((state) => {
            const newUnsavedStackFilters = { ...state.unsavedStackFilters };
            delete newUnsavedStackFilters[stackFilterId];
            const newPendingStackFilters = { ...state.pendingStackFilters };
            delete newPendingStackFilters[stackFilterId];

            // If we're resetting the current filter, clear it
            const newState: Partial<DocumentViewState> = {
              unsavedStackFilters: newUnsavedStackFilters,
              pendingStackFilters: newPendingStackFilters,
            };

            if (state.currentStackFilterId === stackFilterId) {
              newState.currentStackFilterId = '';
            }

            return newState;
          });
        },
        setPendingState: (stackFilterId: string, pending?: boolean) => {
          if (pending) {
            set((state) => {
              if (!state.unsavedStackFilters[stackFilterId]) return state;
              const newPendingStackFilters = { ...state.pendingStackFilters };
              newPendingStackFilters[stackFilterId] =
                state.unsavedStackFilters[stackFilterId];
              const newUnsavedStackFilters = { ...state.unsavedStackFilters };
              delete newUnsavedStackFilters[stackFilterId];
              return {
                pendingStackFilters: newPendingStackFilters,
                unsavedStackFilters: newUnsavedStackFilters,
              };
            });
          } else {
            set((state) => {
              if (!state.pendingStackFilters[stackFilterId]) return state;
              const newUnsavedStackFilters = { ...state.unsavedStackFilters };
              newUnsavedStackFilters[stackFilterId] =
                state.pendingStackFilters[stackFilterId];
              const newPendingStackFilters = { ...state.pendingStackFilters };
              delete newPendingStackFilters[stackFilterId];
              return {
                pendingStackFilters: newPendingStackFilters,
                unsavedStackFilters: newUnsavedStackFilters,
              };
            });
          }
        },
        resetPendingStackFilter: (stackFilterId: string) => {
          set((state) => {
            const newPendingStackFilters = { ...state.pendingStackFilters };
            delete newPendingStackFilters[stackFilterId];
            return {
              pendingStackFilters: newPendingStackFilters,
            };
          });
        },
      },
    }),
    {
      name: 'document-view-store',
      partialize: (state) => {
        const persistedState: Partial<DocumentViewState> = { ...state };
        delete persistedState.actions;
        return persistedState;
      },
      onRehydrateStorage: () => (state) => {
        // If there is no state, nothing to do
        if (!state) return;

        // If there is a currentStackFilterId but no matching filter in unsavedStackFilters,
        // clear it to prevent invalid requests
        if (
          state.currentStackFilterId &&
          !state.unsavedStackFilters[state.currentStackFilterId]
        ) {
          state.currentStackFilterId = '';
        }
      },
    },
  ),
);
